<script setup>
import BaseButton from "@/app/components/ui/BaseButton.vue"
import { useI18n } from "@/app/composables/useI18n"

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  message: {
    type: String,
    default: "",
  },
  confirmText: {
    type: String,
    default: "Delete",
  },
  cancelText: {
    type: String,
    default: "Cancel",
  },
})

const emit = defineEmits(["close", "confirm"])

const { t } = useI18n()

function closeModal() {
  emit("close")
}

function confirmAction() {
  emit("confirm")
  closeModal()
}
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask" data-testid="confirmDeleteModal">
      <div class="modal-container">
        <div class="modal-detail">
          <div class="close-btn" @click="closeModal">
            <div>{{ t("general.close") || "Close" }}</div>
            <img src="/assets/images/close-modal-icon.svg" alt="">
          </div>

          <div class="modal-body">
            <div class="modal-header">
              <h3>{{ title || (t("common.confirm_deletion") || "Confirm Deletion") }}</h3>
            </div>

            <div class="modal-content">
              <p class="confirmation-message">
                {{ message || (t("common.delete_confirmation") || "Are you sure you want to delete this item?") }}
              </p>
            </div>

            <div class="modal-actions">
              <BaseButton class="delete-btn" @click="confirmAction">
                {{ (t("common.delete") || "Delete") }}
              </BaseButton>
              <BaseButton class="cancel-btn" @click="closeModal">
                {{ (t("common.cancel") || "Cancel") }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 24px;
}

.modal-header {
  margin-bottom: 24px;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--be-primary-text-color);
    text-align: center;
  }
}

.modal-content {
  margin-bottom: 32px;

  .confirmation-message {
    font-size: 16px;
    color: var(--be-primary-text-color);
    text-align: center;
    line-height: 1.5;
    margin: 0;
  }
}

.modal-actions {
  display: flex;
  gap: 12px;

  .cancel-btn {
    flex: 1;
    background: var(--be-secondary-btn);
    color: var(--be-secondary-btn-label);

    &:hover {
      background: var(--be-secondary-btn-hover);
    }
  }

  .delete-btn {
    flex: 1;
    background: #f44336 !important;
    color: #ffffff !important;
    border: 1px solid #f44336 !important;
    border-radius: 4px !important;

    &:hover {
      background: #d32f2f !important;
      border-color: #d32f2f !important;
    }

    &:active {
      background: #c62828 !important;
      border-color: #c62828 !important;
    }

    &:focus {
      outline: none !important;
      box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.3) !important;
    }
  }
}

// Modal transition styles
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

@media screen and (max-width: 768px) {
  .modal-container {
    width: 95%;
    padding: 20px 10px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-actions {
    flex-direction: column;

    .cancel-btn,
    .delete-btn {
      flex: 1;
    }
  }
}
</style>
